import React, { useState } from 'react';
import { CancelTokenSource, fetchApi } from '@api/fetchApi';
import { PaginationParams } from '@components-shared/table/TablePagination/interfaces';
import TableSettings from '@components-shared/table/TableSettings';
import { NUMBER_OF_ROWS, TABLE_CACHE_PREFIX } from '@components-shared/table/constants';
import { ApiGetTotals, TableState } from '@components-shared/table/types';
import { keyMap } from '@locales/lokalise/keyMap';
import { handleError } from '@utils/errors';
import { cacheService } from '@utils/library';
import { ITableRecordData } from 'global.interfaces';
import { Pagination } from './Pagination';

interface ITablePagination {
  apiGetTotals?: ApiGetTotals;
  truncatePagination?: boolean;
  startIndex: number;
  count: number;
  changePage: (page: number, numberOfRows?: number) => ITableRecordData[];
  numberOfRows: number;
  getFetchBody: () => unknown;
  source?: CancelTokenSource;
  updateTableState: (state: Partial<TableState>) => void;
  api: string;
  showSettings: boolean;
  totalsData: unknown;
  tableId: string;
}

export const TablePagination = ({
  apiGetTotals,
  truncatePagination,
  changePage,
  count,
  startIndex,
  numberOfRows,
  getFetchBody,
  source,
  updateTableState,
  showSettings,
  api,
  tableId,
  totalsData,
}: ITablePagination) => {
  const [fetchTotalsProcessing, setFetchTotalsProcessing] = useState<boolean>(false);

  const paginationParams: PaginationParams = {
    count,
    startIndex,
    changePage,
    truncatePagination,
  };

  const handleGetTotals = async () => {
    const fetchBody = getFetchBody();

    if (!apiGetTotals) {
      return;
    }

    try {
      setFetchTotalsProcessing(true);

      const { data: responseData } = await fetchApi.post<number>(apiGetTotals.url, fetchBody, {
        cancelToken: source?.token,
      });

      void updateTableState({
        preciseCount: apiGetTotals.totalCountExtractor?.(responseData) ?? null,
        totalsData: responseData,
      });
    } catch (error) {
      handleError(keyMap.sharedErrorsFailedToGetRecord, error);
    } finally {
      setFetchTotalsProcessing(false);
    }
  };

  const changeRowsPerPage = (numberOfRows: number) => {
    cacheService.cacheValue<number>(tableId, numberOfRows, TABLE_CACHE_PREFIX, api);

    updateTableState({ numberOfRows });
    changePage(0, numberOfRows);
  };

  return (
    <>
      {showSettings && (
        <TableSettings
          numberOfRowsList={NUMBER_OF_ROWS}
          numberOfRows={numberOfRows}
          changeRowsPerPage={changeRowsPerPage}
        />
      )}

      <Pagination
        paginationParams={paginationParams}
        perPage={numberOfRows}
        isGettingTotals={fetchTotalsProcessing}
        isGettingTotalsFinished={Object.keys(totalsData ?? {}).length !== 0}
        onGetTotals={apiGetTotals && handleGetTotals}
        amounts={totalsData ? apiGetTotals?.totalAmountsExtractor?.(totalsData) : undefined}
      />
    </>
  );
};
